'use client'

import { useRef, useEffect, useState, useCallback, useMemo, memo } from 'react'
import { useRouter } from '@/i18n/navigation'
import Sparkline from './Sparkline'
import { fetchSidebarData, fetchSidebarChart, type SidebarQuote, type SidebarData } from '@/services/sidebarApi'
import { useLocale } from 'next-intl'
import { t } from '@/i18n/translations'
import { useSignalR } from '@/hooks/useSignalR'
import type { PriceUpdate } from '@/services/signalrService'

interface TickerData {
  symbol: string
  price: number
  change: number
  data: number[]
  path?: string
}

/* ───── Ticker Yapılandırması ───── */
const trTickerConfig = [
  { symbol: 'USDTRY',  category: 'forex' as const,       displayName: 'USD/TRY',    path: { pathname: '/forex/[pair]' as const, params: { pair: 'USDTRY' } } },
  { symbol: 'EURTRY',  category: 'forex' as const,       displayName: 'EUR/TRY',    path: { pathname: '/forex/[pair]' as const, params: { pair: 'EURTRY' } } },
  { symbol: 'GOLD',    category: 'commodities' as const,  displayName: 'Altın',      path: { pathname: '/market/[id]' as const, params: { id: 'gold' } } },
  { symbol: 'GAUTRY',  category: 'commodities' as const,  displayName: 'Gram Altın', path: { pathname: '/market/[id]' as const, params: { id: 'gautry' } } },
  { symbol: 'EURUSD',  category: 'forex' as const,       displayName: 'EUR/USD',    path: { pathname: '/forex/[pair]' as const, params: { pair: 'EURUSD' } } },
  { symbol: 'BRENT',   category: 'commodities' as const,  displayName: 'Brent',      path: { pathname: '/market/[id]' as const, params: { id: 'brent' } } },
  { symbol: 'DJI',     category: 'indices' as const,     displayName: 'Dow Jones',  path: { pathname: '/market/[id]' as const, params: { id: 'dji' } } },
  { symbol: 'NDX',     category: 'indices' as const,     displayName: 'NASDAQ',     path: { pathname: '/market/[id]' as const, params: { id: 'ndx' } } },
  { symbol: 'DAX',     category: 'indices' as const,     displayName: 'DAX',        path: { pathname: '/market/[id]' as const, params: { id: 'dax' } } },
]

const enTickerConfig = [
  { symbol: 'USDTRY',  category: 'forex' as const,       displayName: 'USD/TRY',  path: { pathname: '/forex/[pair]' as const, params: { pair: 'USDTRY' } } },
  { symbol: 'EURTRY',  category: 'forex' as const,       displayName: 'EUR/TRY',  path: { pathname: '/forex/[pair]' as const, params: { pair: 'EURTRY' } } },
  { symbol: 'GOLD',    category: 'commodities' as const,  displayName: 'Gold',     path: { pathname: '/market/[id]' as const, params: { id: 'gold' } } },
  { symbol: 'EURUSD',  category: 'forex' as const,       displayName: 'EUR/USD',  path: { pathname: '/forex/[pair]' as const, params: { pair: 'EURUSD' } } },
  { symbol: 'BRENT',   category: 'commodities' as const,  displayName: 'Brent',    path: { pathname: '/market/[id]' as const, params: { id: 'brent' } } },
  { symbol: 'DJI',     category: 'indices' as const,     displayName: 'Dow Jones', path: { pathname: '/market/[id]' as const, params: { id: 'dji' } } },
  { symbol: 'NDX',     category: 'indices' as const,     displayName: 'NASDAQ',   path: { pathname: '/market/[id]' as const, params: { id: 'ndx' } } },
  { symbol: 'DAX',     category: 'indices' as const,     displayName: 'DAX',      path: { pathname: '/market/[id]' as const, params: { id: 'dax' } } },
]

const SETS = 3

function formatPrice(price: number, symbol: string): string {
  if (price === 0) return '—'
  if (symbol === 'EURUSD') return price.toFixed(4).replace('.', ',')
  if (price >= 1000) {
    const formatted = price.toFixed(2)
    const [int, dec] = formatted.split('.')
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + dec
  }
  return price.toFixed(price < 100 ? 4 : 2).replace('.', ',')
}

interface TickerItemProps {
  symbol: string
  price: number
  change: number
  data: number[]
  flash: 'up' | 'down' | null
  path?: string
  onClick?: () => void
}

function getChartColor(change: number): string {
  if (change > 0) return 'var(--chart-up)'
  if (change < 0) return 'var(--chart-down)'
  return 'var(--chart-line)'
}

const TickerItem = memo(function TickerItem({ symbol, price, change, data, flash, onClick }: TickerItemProps) {
  const isPositive = change > 0
  const isNegative = change < 0
  const color = getChartColor(change)
  const arrow = isPositive ? '↑' : isNegative ? '↓' : ''
  const flashClass = flash === 'up' ? 'ticker-flash-up' : flash === 'down' ? 'ticker-flash-down' : ''

  return (
    <div
      className={`ticker-item flex items-center gap-2 md:gap-3 px-2 shrink-0 border-r h-full ${flashClass}`}
      style={{ borderColor: 'var(--border-color)', cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
    >
      <div className="flex flex-col">
        <span className="text-[10px] md:text-[11px] font-medium tracking-wide whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
          {symbol}
        </span>
        <span className="text-[12px] md:text-base font-bold transition-colors duration-300 leading-tight" style={{ color: 'var(--text-main)' }}>
          {formatPrice(price, symbol)}
        </span>
        <span className="text-[10px] md:text-[11px] font-semibold flex items-center gap-0.5 leading-tight" style={{ color }}>
          {arrow} {change >= 0 ? '+' : '-'}%{Math.abs(change).toFixed(2).replace('.', ',')}
        </span>
      </div>
      {data && data.length >= 2 && (
        <div className="hidden md:block">
          <Sparkline data={data} color={color} width={50} height={28} />
        </div>
      )}
    </div>
  )
})

/* ───── Snap Matematik ───── */

// Bir set'in snap noktaları (kümülatif genişlikler): [0, w0, w0+w1, ...]
function buildSnapPoints(widths: number[]): number[] {
  const pts = [0]
  for (const w of widths) pts.push(pts[pts.length - 1] + w)
  return pts
}

// scrollLeft → hangi set, hangi item index
function locatePosition(scrollLeft: number, snapPts: number[], sw: number): { setIdx: number; itemIdx: number } {
  if (sw === 0) return { setIdx: 0, itemIdx: 0 }
  const setIdx = Math.floor(scrollLeft / sw)
  const posInSet = scrollLeft - setIdx * sw
  let closest = 0
  let minD = Math.abs(snapPts[0] - posInSet)
  for (let i = 1; i < snapPts.length; i++) {
    const d = Math.abs(snapPts[i] - posInSet)
    if (d < minD) { minD = d; closest = i }
  }
  return { setIdx, itemIdx: closest }
}

// Set + item index → scrollLeft
function scrollForItem(setIdx: number, itemIdx: number, snapPts: number[], sw: number): number {
  const n = snapPts.length - 1 // item sayısı
  while (itemIdx >= n) { itemIdx -= n; setIdx++ }
  while (itemIdx < 0)  { itemIdx += n; setIdx-- }
  return setIdx * sw + snapPts[itemIdx]
}

/**
 * U2 (Temmuz 2026 denetimi): şerit `ssr: false` ile yükleniyordu; ilk HTML'de sabit
 * yükseklikli boş bir kutuydu (CLS yoktu ama fiyatlar SSR gelmiyordu). Artık layout
 * veriyi sunucuda çekip `initialData` veriyor; useEffect yalnızca tazeleme yapıyor.
 */
type TickerCfg = (typeof trTickerConfig)[number] | (typeof enTickerConfig)[number]

function buildTickerData(cfgs: readonly TickerCfg[], sidebar: SidebarData | null | undefined): TickerData[] {
  const quoteMap: Record<string, SidebarQuote> = {}
  if (sidebar) {
    for (const q of [...sidebar.indices, ...sidebar.forex, ...sidebar.commodities, ...sidebar.stocks]) {
      quoteMap[q.symbol] = q
    }
  }
  return cfgs.map(c => ({
    symbol: c.displayName,
    price: quoteMap[c.symbol]?.value ?? 0,
    change: quoteMap[c.symbol]?.changePercent ?? 0,
    data: [],
  }))
}

type Props = { initialData?: SidebarData | null }

export default function TickerBar({ initialData }: Props) {
  const lang = useLocale()
  const router = useRouter()
  const scrollRef = useRef<HTMLDivElement>(null)

  const tickerConfig = lang === 'tr' ? trTickerConfig : enTickerConfig
  const ITEM_COUNT = tickerConfig.length

  // Ölçümler
  const itemWidthsRef = useRef<number[]>([])
  const snapPtsRef = useRef<number[]>([0])
  const swRef = useRef(0) // set width

  // Data
  const [liveData, setLiveData] = useState<TickerData[]>(() =>
    buildTickerData(tickerConfig, initialData),
  )
  const [flashes, setFlashes] = useState<Record<string, 'up' | 'down' | null>>({})
  const [paused, setPaused] = useState(false)
  const pauseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isAutoScrolling = useRef(false)
  const sparklineCache = useRef<Record<string, number[]>>({})
  const prevPrices = useRef<Record<string, number>>({})

  // Touch state
  const touchStartX = useRef(0)
  const touchStartScroll = useRef(0)
  const touchStartTime = useRef(0)
  const isDragging = useRef(false)
  const isTap = useRef(true)

  /* ───── SignalR Canlı Fiyat ───── */
  const handlePriceUpdate = useCallback((update: PriceUpdate) => {
    const cfgIndex = tickerConfig.findIndex(c => c.symbol === update.symbol)
    if (cfgIndex === -1) return

    setLiveData(prev => {
      const old = prev[cfgIndex]
      if (!old || old.price === update.price) return prev

      if (old.price > 0) {
        const flash = update.price > old.price ? 'up' as const : 'down' as const
        setFlashes(f => ({ ...f, [tickerConfig[cfgIndex].symbol]: flash }))
        setTimeout(() => setFlashes(f => ({ ...f, [tickerConfig[cfgIndex].symbol]: null })), 1000)
      }

      const updated = [...prev]
      updated[cfgIndex] = { ...old, price: update.price }
      prevPrices.current[tickerConfig[cfgIndex].symbol] = update.price
      return updated
    })
  }, [])

  useSignalR({
    onPriceUpdate: handlePriceUpdate,
  })

  const getDisplayName = useCallback((name: string) => {
    if (name === 'Altın') return t('ticker.gold', lang)
    return name
  }, [lang])

  /* ───── Navigasyon helpers ───── */
  const handleTickerClick = useCallback((cfgIdx: number) => {
    const cfg = tickerConfig[cfgIdx]
    if (cfg.path) {
      router.push(cfg.path as any)
    }
  }, [router])

  /* ───── Navigasyon: N item ilerle/geri ───── */
  const navigateItems = useCallback((el: HTMLDivElement, count: number) => {
    const { setIdx, itemIdx } = locatePosition(el.scrollLeft, snapPtsRef.current, swRef.current)
    const target = scrollForItem(setIdx, itemIdx + count, snapPtsRef.current, swRef.current)
    el.scrollTo({ left: target, behavior: 'smooth' })
  }, [])

  /* ───── Loop: Ortaya resetle ───── */
  const resetLoopIfNeeded = useCallback(() => {
    const el = scrollRef.current
    if (!el || swRef.current === 0) return
    const sw = swRef.current
    const pos = el.scrollLeft
    if (pos < sw * 1 || pos > sw * 3.5) {
      const posInSet = pos % sw
      el.style.scrollBehavior = 'auto'
      el.scrollLeft = sw * 2 + posInSet
      requestAnimationFrame(() => { el.style.scrollBehavior = '' })
    }
  }, [])

  /* ───── Resume ───── */
  const scheduleResume = useCallback(() => {
    if (pauseTimer.current) clearTimeout(pauseTimer.current)
    pauseTimer.current = setTimeout(() => setPaused(false), 2000)
  }, [])

  /* ───── Sparkline ───── */
  const fetchSparklines = useCallback(async () => {
    const allCached = tickerConfig.every(c => (sparklineCache.current[c.symbol]?.length ?? 0) > 0)
    if (allCached) return
    const promises = tickerConfig
      .filter(c => !(sparklineCache.current[c.symbol]?.length > 0))
      .map(async (c) => {
        const points = await fetchSidebarChart(c.symbol, '1A')
        if (points.length > 0) sparklineCache.current[c.symbol] = points
      })
    await Promise.allSettled(promises)
  }, [])

  /* ───── Sidebar API ───── */
  const fetchRealData = useCallback(async () => {
    const sidebarData = await fetchSidebarData()
    if (!sidebarData) return
    const quoteMap: Record<string, SidebarQuote> = {}
    for (const q of [...sidebarData.indices, ...sidebarData.forex, ...sidebarData.commodities, ...sidebarData.stocks]) {
      quoteMap[q.symbol] = q
    }
    const newItems: TickerData[] = []
    const newFlashes: Record<string, 'up' | 'down'> = {}
    for (const cfg of tickerConfig) {
      const quote = quoteMap[cfg.symbol]
      const price = quote?.value ?? 0
      const change = quote?.changePercent ?? 0
      const prev = prevPrices.current[cfg.symbol] || 0
      if (prev > 0 && price > 0 && price !== prev) {
        newFlashes[cfg.symbol] = price > prev ? 'up' : 'down'
      }
      if (price > 0) prevPrices.current[cfg.symbol] = price
      newItems.push({
        symbol: getDisplayName(cfg.displayName), price, change,
        data: sparklineCache.current[cfg.symbol] || [],
      })
    }
    setLiveData(newItems)
    if (Object.keys(newFlashes).length > 0) {
      setFlashes(prev => ({ ...prev, ...newFlashes }))
      setTimeout(() => {
        setFlashes(prev => {
          const updated = { ...prev }
          for (const k of Object.keys(newFlashes)) updated[k] = null
          return updated
        })
      }, 1000)
    }
  }, [getDisplayName])

  useEffect(() => {
    fetchSparklines().then(() => fetchRealData())
    const iv = setInterval(() => fetchSparklines().then(() => fetchRealData()), 30_000)
    return () => clearInterval(iv)
  }, [fetchRealData, fetchSparklines])

  const loopedData = Array.from({ length: SETS }, () => liveData).flat()

  // İlk anlamlı veri geldiğinde true olur, bir daha değişmez
  const dataLoaded = useMemo(() => liveData.some(d => d.price > 0), [liveData])

  /* ───── Ölçüm + Başlangıç ───── */
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const raf = requestAnimationFrame(() => {
      const items = el.querySelectorAll('.ticker-item')
      const widths: number[] = []
      for (let i = 0; i < ITEM_COUNT && i < items.length; i++) {
        widths.push(items[i].getBoundingClientRect().width)
      }
      if (widths.length === 0) return
      itemWidthsRef.current = widths
      snapPtsRef.current = buildSnapPoints(widths)
      swRef.current = widths.reduce((a, b) => a + b, 0)
      el.style.scrollBehavior = 'auto'
      el.scrollLeft = swRef.current * 2
      el.style.scrollBehavior = ''
    })
    return () => cancelAnimationFrame(raf)
  }, [dataLoaded])

  /* ───── Otomatik kaydırma ───── */
  useEffect(() => {
    if (paused) return
    const el = scrollRef.current
    if (!el || swRef.current === 0) return
    const interval = setInterval(() => {
      isAutoScrolling.current = true
      navigateItems(el, 1)
      setTimeout(() => {
        resetLoopIfNeeded()
        isAutoScrolling.current = false
      }, 600)
    }, 2500)
    return () => clearInterval(interval)
  }, [paused, navigateItems, resetLoopIfNeeded])

  /* ───── Touch: Manuel drag (momentum yok) + snap ───── */
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const onTouchStart = (e: TouchEvent) => {
      isDragging.current = true
      isTap.current = true
      touchStartX.current = e.touches[0].clientX
      touchStartScroll.current = el.scrollLeft
      touchStartTime.current = Date.now()

      // Devam eden animasyonu durdur
      el.style.scrollBehavior = 'auto'
      el.scrollLeft = el.scrollLeft

      setPaused(true)
      if (pauseTimer.current) clearTimeout(pauseTimer.current)
    }

    const onTouchMove = (e: TouchEvent) => {
      if (!isDragging.current) return
      const dx = touchStartX.current - e.touches[0].clientX
      // 8px'den az hareket → tap sayılır, preventDefault yapma (click çalışsın)
      if (Math.abs(dx) > 8) {
        isTap.current = false
        e.preventDefault()
        el.scrollLeft = touchStartScroll.current + dx
      }
    }

    const onTouchEnd = (e: TouchEvent) => {
      if (!isDragging.current) return
      isDragging.current = false
      el.style.scrollBehavior = ''

      // Tap ise snap/scroll yapma, click event'inin doğal yoldan tetiklenmesini bekle
      if (isTap.current) {
        scheduleResume()
        return
      }

      const dx = touchStartX.current - e.changedTouches[0].clientX
      const dt = Math.max(Date.now() - touchStartTime.current, 1)
      const velocity = dx / dt // px/ms — pozitif = sola swipe

      // Hıza göre kaç item ilerleyecek (max 3)
      let advance = 0
      const absV = Math.abs(velocity)
      if (absV > 0.8) advance = 3
      else if (absV > 0.4) advance = 2
      else if (absV > 0.15) advance = 1

      // Yön
      if (velocity < 0) advance = -advance

      // Az sürüklendiyse ama 30px+ geçtiyse en az 1 item
      if (advance === 0 && Math.abs(dx) > 30) {
        advance = dx > 0 ? 1 : -1
      }

      // Snap hedefi: sürükleme başlangıcındaki snap noktasından N item ilerle
      const { setIdx, itemIdx } = locatePosition(touchStartScroll.current, snapPtsRef.current, swRef.current)
      const target = scrollForItem(setIdx, itemIdx + advance, snapPtsRef.current, swRef.current)
      el.scrollTo({ left: target, behavior: 'smooth' })

      setTimeout(() => resetLoopIfNeeded(), 500)
      scheduleResume()
    }

    // Touch'ları passive: false ile ekle (preventDefault çalışsın)
    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd, { passive: true })

    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
    }
  }, [resetLoopIfNeeded, scheduleResume])

  /* ───── Wheel: Her tick = 1 item, snap'li ───── */
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    let wheelTimer: ReturnType<typeof setTimeout> | null = null

    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) < Math.abs(e.deltaX)) return
      e.preventDefault()

      setPaused(true)
      if (pauseTimer.current) clearTimeout(pauseTimer.current)

      const dir = e.deltaY > 0 ? 1 : -1
      navigateItems(el, dir)

      if (wheelTimer) clearTimeout(wheelTimer)
      wheelTimer = setTimeout(() => {
        resetLoopIfNeeded()
        scheduleResume()
      }, 400)
    }

    el.addEventListener('wheel', onWheel, { passive: false })
    return () => {
      el.removeEventListener('wheel', onWheel)
      if (wheelTimer) clearTimeout(wheelTimer)
    }
  }, [navigateItems, resetLoopIfNeeded, scheduleResume])

  /* ───── Desktop ok butonları ───── */
  const scrollByItem = useCallback((dir: 'left' | 'right') => {
    const el = scrollRef.current
    if (!el) return
    setPaused(true)
    if (pauseTimer.current) clearTimeout(pauseTimer.current)
    isAutoScrolling.current = true
    navigateItems(el, dir === 'right' ? 1 : -1)
    setTimeout(() => {
      resetLoopIfNeeded()
      isAutoScrolling.current = false
      scheduleResume()
    }, 500)
  }, [navigateItems, resetLoopIfNeeded, scheduleResume])

  return (
    <div className="flex items-center md:px-4" style={{ minHeight: '46px' }}>
      <div
        ref={scrollRef}
        className="ticker-scroll-container flex items-center h-[46px] md:h-[72px] overflow-x-auto flex-1"
        style={{ scrollbarWidth: 'none', touchAction: 'pan-y pinch-zoom' }}
      >
        {loopedData.map((item, i) => (
          <TickerItem
            key={`${item.symbol}-${i}`}
            {...item}
            flash={flashes[item.symbol] || null}
            onClick={() => handleTickerClick(i % ITEM_COUNT)}
          />
        ))}
      </div>

      <div className="hidden md:flex items-center shrink-0 ml-2 gap-1">
        <button
          onClick={() => scrollByItem('left')}
          aria-label={t('aria.prevTicker', lang)}
          className="w-8 h-12 flex items-center justify-center border-none cursor-pointer transition-opacity duration-200 hover:opacity-50"
          style={{ background: 'transparent', color: 'var(--text-main)' }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button
          onClick={() => scrollByItem('right')}
          aria-label={t('aria.nextTicker', lang)}
          className="w-8 h-12 flex items-center justify-center border-none cursor-pointer transition-opacity duration-200 hover:opacity-50"
          style={{ background: 'transparent', color: 'var(--text-main)' }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>
    </div>
  )
}
